import React, { useEffect, useRef } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { ExclamationTriangleIcon, InfoCircledIcon } from '@radix-ui/react-icons'

import type { PaytrieAPI } from '@paytrie/client'
import { LoadingIcon } from '@/components/LoadingIcon'
import { ProgressSteps } from '@/components/ProgressSteps'
import { DropdownMenuSelect } from '@/components/widget-components'
import { WidgetLayout } from '@/pages/Buy/WidgetLayout'
import { OtcContactSupport } from '@/components/OtcContactSupport'
import { WireInstructions } from '@/components/WireInstructions'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { useLinkedTransaction, useOtcRequest } from '@/hooks/useOtc'
import { isBuyOrder } from '@/constants/otc'
import { formatMoney } from '@paytrie/common/utils'
import { formatCurrency } from '@/pages/Dashboard/dashboardDisplayUtils'
import {
  getIsOtcAwaitingUser,
  getOtcStepIndex,
  getOtcStepStatuses,
  getOtcTone,
  toOtcStatusLabel,
} from '@/pages/Dashboard/OtcRequests/otcRequestUtils'
import { OtcStatusBadge } from '@/pages/Dashboard/OtcRequests/OtcStatusBadge'
import {
  trackOtcRequestSettled,
  trackOtcRequestStatusViewed,
} from '@/utils/analytics'

type SummaryRowProps = { label: string; children: React.ReactNode }

const SummaryRow = ({ label, children }: SummaryRowProps) => (
  <div className="flex flex-col gap-0.5 py-1.5 sm:flex-row sm:justify-between sm:gap-4 sm:py-1">
    <span className="shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
    <span className="break-all font-medium text-black dark:text-white sm:text-right">
      {children}
    </span>
  </div>
)

const getStatusMessageKey = (request: PaytrieAPI.OtcRequest) => {
  if (request.status === 'cancelled') {
    return request.cancelledAt
      ? 'otcRequestStatus.message.cancelled'
      : 'otcRequestStatus.message.closed'
  }
  return `otcRequestStatus.message.${request.status}`
}

export function OtcRequestStatus() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { enableOtc, isLoading: flagsLoading } = useFeatureFlags()
  const { data: request, isLoading, isError } = useOtcRequest(id)
  const { data: linkedTransaction } = useLinkedTransaction(
    request?.transactionId ?? null
  )

  const lastTrackedStatus = useRef<string | null>(null)
  useEffect(() => {
    if (!request) return
    const statusKey = `${request.id}:${request.status}`
    if (lastTrackedStatus.current === statusKey) return
    lastTrackedStatus.current = statusKey

    const side = isBuyOrder(request.leftSideLabel) ? 'buy' : 'sell'
    const event = {
      otcRequestId: request.id,
      status: request.status,
      side,
    } as const

    trackOtcRequestStatusViewed(event)
    if (getOtcTone(request.status) !== 'pending') {
      trackOtcRequestSettled(event)
    }
  }, [request])

  if (flagsLoading || isLoading) {
    return (
      <div className="flex justify-center p-10">
        <LoadingIcon />
      </div>
    )
  }

  if (!enableOtc) {
    return <Navigate to="/" replace />
  }

  if (isError || !request) {
    return (
      <WidgetLayout>
        <div className="flex justify-end">
          <DropdownMenuSelect />
        </div>
        <h2
          className="text-lg font-normal mb-6 text-center text-black dark:text-white mt-12"
          data-testid="text-otc-status-title"
        >
          {t('otc.status.pageTitle')}
        </h2>
        <div
          className="flex flex-col items-center justify-center px-4 sm:px-6"
          data-testid="error-otc-status"
        >
          <div className="w-full max-w-md text-center">
            <div className="p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                {t('otc.status.notFound')}
              </h3>
              <OtcContactSupport screen="status-not-found" />
            </div>
          </div>
        </div>
      </WidgetLayout>
    )
  }

  const { status } = request
  const isBuy = isBuyOrder(request.leftSideLabel)
  const stepStatuses = getOtcStepStatuses(isBuy ? 'buy' : 'sell')
  const stepIndex = getOtcStepIndex(status, isBuy ? 'buy' : 'sell')
  const tone = getOtcTone(status)
  const isAwaitingUser = getIsOtcAwaitingUser(status)
  const statusMessageKey = getStatusMessageKey(request)
  const sentAmount = `${formatMoney(request.leftSideValue)} ${formatCurrency(request.leftSideLabel)}`
  const receivedAmount = linkedTransaction
    ? `${formatMoney(linkedTransaction.rightSideValue)} ${formatCurrency(linkedTransaction.rightSideLabel)}`
    : formatCurrency(request.rightSideLabel)

  return (
    <WidgetLayout>
      <div className="flex justify-end">
        <DropdownMenuSelect />
      </div>

      <div className="mt-16 flex items-center justify-between md:mt-12">
        <h1 className="text-lg font-semibold uppercase">
          {t('otc.status.orderTitle')}
        </h1>
        <OtcStatusBadge
          status={status}
          cancelledAt={request.cancelledAt}
          testId="text-otc-status"
        />
      </div>

      {stepIndex !== null && (
        <ProgressSteps
          labels={stepStatuses.map((stepStatus) =>
            t(`dashboardOtcRequests.status.${stepStatus}`, {
              defaultValue: toOtcStatusLabel(stepStatus),
            })
          )}
          currentIndex={stepIndex}
          isComplete={tone === 'success'}
        />
      )}

      {statusMessageKey && (
        <div
          className={clsx(
            'flex items-start gap-2 rounded-lg p-3 text-sm',
            isAwaitingUser &&
              'bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100',
            !isAwaitingUser &&
              tone === 'failed' &&
              'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
            !isAwaitingUser &&
              tone !== 'failed' &&
              'bg-gray-50 text-gray-600 dark:bg-darkCharcoal dark:text-gray-300'
          )}
          data-testid="text-otc-status-message"
        >
          {isAwaitingUser ? (
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <InfoCircledIcon className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div>
            {isAwaitingUser && (
              <p className="font-medium">
                {t('otcRequestStatus.awaitingWire')}
              </p>
            )}
            <p className={clsx(isAwaitingUser && 'mt-0.5')}>
              {t(statusMessageKey, {
                amount: sentAmount,
                date: request.cancelledAt
                  ? dayjs(request.cancelledAt).format('MMM D, YYYY')
                  : '',
              })}
            </p>
          </div>
        </div>
      )}

      {isBuy && isAwaitingUser && (
        <WireInstructions
          amount={request.leftSideValue}
          currency={request.leftSideLabel}
        />
      )}

      <div
        className="rounded border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-pebble"
        data-testid="container-otc-summary"
      >
        <SummaryRow label={t('otc.summary.youSend')}>{sentAmount}</SummaryRow>
        <SummaryRow label={t('otc.summary.youReceive')}>
          {receivedAmount}
        </SummaryRow>
        <SummaryRow
          label={
            isBuy
              ? t('otc.summary.receivingWallet')
              : t('otc.summary.sendingWallet')
          }
        >
          <span className="font-mono text-xs">{request.wallet ?? '—'}</span>
        </SummaryRow>
        <SummaryRow label={t('otc.summary.date')}>
          {dayjs(request.createdAt).format('MMM D, YYYY h:mm A')}
        </SummaryRow>
        {request.transactionId && (
          <SummaryRow label={t('otc.status.relatedTransaction')}>
            <Link
              to={`/Status?id=${request.transactionId}`}
              className="font-mono text-xs text-blue-500 hover:underline"
              data-testid="link-otc-transaction"
            >
              {request.transactionId}
            </Link>
          </SummaryRow>
        )}
      </div>

      <div className="mb-10">
        <OtcContactSupport screen="status" />
      </div>
    </WidgetLayout>
  )
}

export default React.memo(OtcRequestStatus)
